import { logger } from '../../utils/logger';
import { authenticatedFetch } from '../../utils/authenticatedFetch';
import { getV2AuthHeaders } from '../../utils/authHeaders';
import { getEnv } from '../../config/env';

export interface Division {
  id: string;
  name: string;
}

interface DivisionApiResponse {
  id: string;
  name: string;
}

/**
 * Set to true to use mock division data instead of hitting the API.
 * Flip to false to use the real backend endpoint.
 */
export const USE_MOCK_DIVISIONS = false;

const MOCK_DIVISIONS: Division[] = [
  { id: 'div-001', name: 'Demo Division' },
  { id: 'div-002', name: 'Sales Division' },
  { id: 'div-003', name: 'Support Division' },
  { id: 'div-004', name: 'QA Division' },
  { id: 'div-005', name: 'Training Division' },
];

export const fetchDivisionsFromService = async (
  bearerToken: string,
  orgId: string
): Promise<Division[]> => {
  if (USE_MOCK_DIVISIONS) {
    logger.info('[MOCK] Returning mock divisions', { orgId, count: MOCK_DIVISIONS.length });
    return Promise.resolve(MOCK_DIVISIONS);
  }

  try {
    const baseUrl = getEnv().VITE_EDCI_API_URL;
    const url = `${baseUrl}/api/organizations/${orgId}/divisions`;

    const response = await authenticatedFetch(url, {
      headers: getV2AuthHeaders(bearerToken)
    });

    if (!response.ok) {
      logger.error('Fetch divisions API returned error', {
        status: response.status,
        statusText: response.statusText,
        orgId,
        url
      });
      throw new Error(`Error fetching divisions: ${response.status} ${response.statusText}`);
    }

    const data: DivisionApiResponse[] = await response.json();
    logger.debug('Divisions API response received', {
      orgId,
      count: data.length
    });

    const mapped: Division[] = data.map((d) => ({ id: d.id, name: d.name }));

    logger.info('Divisions fetched successfully', {
      orgId,
      count: mapped.length
    });

    return mapped;
  } catch (error) {
    const msg = error instanceof Error ? error.message : String(error);
    logger.error('Failed to fetch divisions', {
      error: msg,
      orgId
    });
    throw error;
  }
};
